import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';
import { 
  Users,
  Database,
  DollarSign,
  TrendingUp,
  Activity,
  CreditCard,
  UserPlus,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (e) {
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStats();
  };

  if (loading) {
    return (
      <AdminLayout title="Dashboard" subtitle="Cargando estadísticas...">
        <div className="flex items-center justify-center h-64">
          <Loader2 size={40} className="text-amber-400 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const statCards = [
    {
      title: 'Total Usuarios',
      value: stats?.total_users || 0,
      icon: Users,
      color: 'from-blue-500 to-blue-600',
      change: '+12%',
      changeType: 'positive'
    },
    {
      title: 'Usuarios Activos',
      value: stats?.active_subscriptions || 0,
      icon: UserPlus,
      color: 'from-green-500 to-green-600',
      change: '+8%',
      changeType: 'positive'
    },
    {
      title: 'Ingresos Totales',
      value: `$${(stats?.total_revenue || 0).toFixed(2)}`,
      icon: DollarSign,
      color: 'from-amber-500 to-orange-600',
      change: '+23%',
      changeType: 'positive'
    },
    {
      title: 'Loterías Activas',
      value: stats?.total_lotteries || 0,
      icon: Database,
      color: 'from-purple-500 to-purple-600',
      change: '0%',
      changeType: 'neutral'
    },
  ];

  return (
    <AdminLayout title="Dashboard" subtitle="Resumen general del sistema">
      {/* Refresh Button */}
      <div className="flex justify-end mb-6">
        <Button 
          onClick={handleRefresh} 
          variant="outline" 
          className="border-gray-700 text-gray-300 hover:bg-gray-800"
          disabled={refreshing}
        >
          <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label || `stat-${idx}`} className="bg-gray-900 border-gray-800 overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-white">{stat.value}</p>
                    <div className="flex items-center gap-1 mt-2">
                      {stat.changeType === 'positive' ? (
                        <ArrowUpRight size={14} className="text-green-400" />
                      ) : stat.changeType === 'negative' ? (
                        <ArrowDownRight size={14} className="text-red-400" />
                      ) : null}
                      <span className={`text-xs ${
                        stat.changeType === 'positive' ? 'text-green-400' : 
                        stat.changeType === 'negative' ? 'text-red-400' : 'text-gray-500'
                      }`}>
                        {stat.change} este mes
                      </span>
                    </div>
                  </div>
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}>
                    <Icon size={24} className="text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Total Resultados */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Activity size={20} className="text-cyan-400" />
              Resultados Totales
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold text-white">{stats?.total_results || 0}</p>
            <p className="text-sm text-gray-500 mt-1">Sorteos registrados</p>
          </CardContent>
        </Card>

        {/* Recomendaciones */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <TrendingUp size={20} className="text-pink-400" />
              Recomendaciones
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold text-white">{stats?.total_recommendations || 0}</p>
            <p className="text-sm text-gray-500 mt-1">Generadas en total</p>
          </CardContent>
        </Card>

        {/* Transacciones */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <CreditCard size={20} className="text-amber-400" />
              Transacciones
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold text-white">{stats?.total_transactions || 0}</p>
            <p className="text-sm text-gray-500 mt-1">Pagos procesados</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Recommendations */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Recomendaciones Recientes</CardTitle>
            <CardDescription className="text-gray-500">Últimas 5 recomendaciones generadas</CardDescription>
          </CardHeader>
          <CardContent>
            {stats?.recent_recommendations?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_recommendations.slice(0, 5).map((rec, idx) => (
                  <div key={rec.id || `rec-${idx}`} className="flex items-center justify-between p-3 rounded-lg bg-gray-800/50">
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">
                        {rec.numbers?.slice(0, 3).map((num, i) => (
                          <span key={`num-${num}-${i}`} className="w-8 h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-xs font-bold">
                            {num}
                          </span>
                        ))}
                      </div>
                      <div>
                        <p className="text-sm text-white font-medium">{rec.lottery_code}</p>
                        <p className="text-xs text-gray-500">{rec.strategy}</p>
                      </div>
                    </div>
                    <Badge className="bg-gray-700 text-gray-300">
                      {new Date(rec.created_at).toLocaleDateString('es-DO')}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No hay recomendaciones recientes</p>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Acciones Rápidas</CardTitle>
            <CardDescription className="text-gray-500">Accesos directos a funciones comunes</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link 
              to="/admin/users" 
              className="flex items-center justify-between p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 hover:bg-blue-500/20 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Users size={20} className="text-blue-400" />
                <span className="text-white font-medium">Gestionar Usuarios</span>
              </div>
              <ArrowUpRight size={18} className="text-blue-400 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>

            <Link 
              to="/admin/lotteries" 
              className="flex items-center justify-between p-4 rounded-xl bg-purple-500/10 border border-purple-500/20 hover:bg-purple-500/20 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Database size={20} className="text-purple-400" />
                <span className="text-white font-medium">Administrar Loterías</span>
              </div>
              <ArrowUpRight size={18} className="text-purple-400 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>

            <Link 
              to="/admin/system" 
              className="flex items-center justify-between p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 hover:bg-amber-500/20 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Activity size={20} className="text-amber-400" />
                <span className="text-white font-medium">Estado del Sistema</span>
              </div>
              <ArrowUpRight size={18} className="text-amber-400 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>

            <Link 
              to="/admin/transactions" 
              className="flex items-center justify-between p-4 rounded-xl bg-green-500/10 border border-green-500/20 hover:bg-green-500/20 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <CreditCard size={20} className="text-green-400" />
                <span className="text-white font-medium">Ver Transacciones</span>
              </div>
              <ArrowUpRight size={18} className="text-green-400 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
